import React, { useMemo, useState, useRef, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { FaPlus, FaMinus } from 'react-icons/fa';
import './ExplorePacks.css';
import '../MessageCodes/A3PackDetails.css';
import apiService from '../../Services/apiServices';
import iconPlus from '../../Images/IconPlus.svg';
import iconMinus from '../../Images/IconMinus.svg';
import ScrollArrows from './ScrollArrows';
import FdoPopup from './FdoPopup';
import AGENTASSISTPOPUP from '../MessageCodes/AGENTASSISTPOPUP';

const FilterDropdown = ({ values, selected, onChange, placeholder = "Filter" }) => {
    const [open, setOpen] = useState(false);
    const [query, setQuery] = useState("");
    const [tempSelected, setTempSelected] = useState(new Set());

    const visibleValues = useMemo(() => {
        const q = query.trim().toLowerCase();
        return q ? values.filter((v) => String(v).toLowerCase().includes(q)) : values;
    }, [values, query]);

    const toggleValue = (v) => {
        const next = new Set(tempSelected);
        if (next.has(v)) next.delete(v);
        else next.add(v);
        setTempSelected(next);
    };

    const handleOpen = () => {
        setTempSelected(new Set(selected));
        setOpen(true);
    };

    const handleCancel = () => {
        setTempSelected(new Set());
        setQuery("");
        setOpen(false);
    };

    const handleProceed = () => {
        onChange(tempSelected);
        setQuery("");
        setOpen(false);
    };

    return (
        <div className="filter-wrap">
            <button type="button" className={`filter-btn ${selected.size ? "active" : ""}`} onClick={handleOpen} title="Filter">
                {selected.size > 0 ? "✅" : "▾"}
            </button>
            {open && createPortal(
                <div className="filter-overlay">
                    <div className="filter-dropdown">
                        <div className="filter-actions">
                            <button type="button" onClick={() => setTempSelected(new Set(visibleValues))} className="mini-btn">Select all</button>
                            <button type="button" onClick={() => { setQuery(""); setTempSelected(new Set()); }} className="mini-btn">Clear</button>
                        </div>
                        <input className="filter-search" value={query} placeholder={placeholder} onChange={(e) => setQuery(e?.target?.value)} />
                        <div className="filter-list">
                            {visibleValues.map((v, i) => (
                                <label key={i} className="packs-filter-item A3f" onClick={(e) => e.stopPropagation()}>
                                    <input type="checkbox" checked={tempSelected.has(v)} onChange={(e) => { e.stopPropagation(); toggleValue(v); }} />
                                    <span>{v || "(blank)"}</span>
                                </label>
                            ))}
                        </div>
                        <div className="filter-footer">
                            <span className="filter-status">{tempSelected.size ? `${tempSelected.size} of ${values.length} selected` : "No filter"}</span>
                            <div className="filter-buttons">
                                <button type="button" className="cancel-btn" onClick={handleCancel}>Cancel</button>
                                <button type="button" className="proceed-btn" onClick={handleProceed}>Proceed</button>
                            </div>
                        </div>
                    </div>
                </div>,
                document.body
            )}
        </div>
    );
};

const ExplorePacks = ({ header, packDetails, sessionId: sessionIdProp, errors, setErrors, index, showMaximumChat, disabled, showMaxScreen, setIsShowMaximumChat, onFdoChange, globalLockRule, onLockRuleChange }) => {
    const sessionId = useMemo(() => {
        return sessionIdProp || (typeof window !== 'undefined' ? window.sessionStorage.getItem('inputSessionId') : undefined);
    }, [sessionIdProp]);

    const categoryId = packDetails?.categoryId || '';
    const initialMapping = packDetails?.mapping || {};
    const initialHeadingOrder = packDetails?.headingOrder || [];
    const [mapping, setMapping] = useState(initialMapping);
    const [headingOrder, setHeadingOrder] = useState(initialHeadingOrder);
    const columnOrder = (headingOrder?.length > 0) ? headingOrder : ["friendlyName", "packName", "serviceType", "packType", "channelNumber", "language", "genre", "startDate", "endDate", "source", "channelCount", "channelList", "rentalCounter", "mbr", "status"];
    const orderedMapping = columnOrder.reduce((acc, key) => {
        if (mapping[key]) {
            const hasNewFields = mapping['channelNumber'] && mapping['language'] && mapping['genre'];
            if (hasNewFields && (key === 'serviceType' || key === 'packType')) {
                return acc;
            }
            acc[key] = mapping[key];
        }
        return acc;
    }, {});
    const exploreTiles = Array.isArray(packDetails?.explorePacks) ? packDetails.explorePacks : [];
    const initialPacks = Array.isArray(packDetails?.packs) ? packDetails.packs : [];
    const initialIsAddRequest = !!packDetails?.isAddRequest;
    const initialIsDropRequest = !!packDetails?.isDropRequest;

    const [packs, setPacks] = useState(initialPacks);
    const [currentTitle, setCurrentTitle] = useState(header);
    const [isAddRequest, setIsAddRequest] = useState(initialIsAddRequest);
    const [isDropRequest, setIsDropRequest] = useState(initialIsDropRequest);
    const [totalPages, setTotalPages] = useState(packDetails?.totalPages || 1);
    const [currentPage, setCurrentPage] = useState(packDetails?.currentPage || 1);
    const [activeTile, setActiveTile] = useState(exploreTiles[0]?.packType || 'default');
    const [cachedData, setCachedData] = useState(() => {
        const firstTile = exploreTiles[0]?.packType;
        if (firstTile && initialPacks.length > 0) {
            return {
                [`${firstTile}-1`]: {
                    packs: initialPacks,
                    name: header,
                    isAddRequest: initialIsAddRequest,
                    isDropRequest: initialIsDropRequest,
                    disabledCheckBox: !!packDetails?.disabledCheckBox,
                    totalPages: packDetails?.totalPages || 1
                }
            };
        }
        return {};
    });
    const [cachedSelections, setCachedSelections] = useState({});
    const [subFilters, setSubFilters] = useState({});
    const [loadingDetails, setLoadingDetails] = useState(false);
    const [modalData, setModalData] = useState(null);
    const [collapsed, setCollapsed] = useState(true);
    const [sortConfig, setSortConfig] = useState({});
    const [renderKey, setRenderKey] = useState(0);
    const [isTableHorizontallyScrolled, setIsTableHorizontallyScrolled] = useState(false);
    const scrollRef = useRef(null);
    const tilesScrollRef = useRef(null);
    const [isHeaderCollapsed, setIsHeaderCollapsed] = useState(false);
    const [showTooltip, setShowTooltip] = useState(false);
    const [selectedMonth, setSelectedMonth] = useState(() => {
        const now = new Date();
        const lastMonth = now.getMonth(); // 0-11, so current month - 1
        return lastMonth === 0 ? 12 : lastMonth;
    });
    const [selectedYear, setSelectedYear] = useState(() => {
        const now = new Date();
        return now.getMonth() === 0 ? now.getFullYear() - 1 : now.getFullYear();
    });
    const [noDataMessage, setNoDataMessage] = useState('');
    const [fdoPopup, setFdoPopup] = useState(null);
    const [fdoDisabledPacks, setFdoDisabledPacks] = useState(new Set());
    const [uspPopup, setUspPopup] = useState(null);
    const popUpComponent = packDetails?.popUpComponent || [];

    

    const currentSelections = cachedSelections[activeTile] || new Set();
    const hasAnyCheckbox = useMemo(() => packs.some(pack => pack.isCheckBox), [packs]);

    const getPackName = (pack) =>
        pack?.packName || pack?.serviceType || `${pack?.startDate}-${pack?.mbr}`;

    const isEnabledFlag = (value) => value === true || value === 'true';
    const isLockInPack = (pack) => isEnabledFlag(pack?.isTPLockInPack);
    const isLockOutPack = (pack) => isEnabledFlag(pack?.isTPLockOutPack);
    const isSourceEnabledPack = (pack) => isEnabledFlag(pack?.isSourceEnabled);

    const findPackByName = (packName) => {
        const visiblePack = packs.find(pack => getPackName(pack) === packName);
        if (visiblePack) return visiblePack;

        for (const cached of Object.values(cachedData)) {
            const cachedPack = cached?.packs?.find(pack => getPackName(pack) === packName);
            if (cachedPack) return cachedPack;
        }
        return null;
    };

    const getLockSelectionRule = (selections) => {
        const selectedPacks = Array.from(selections)
            .map(findPackByName)
            .filter(Boolean);
        const exclusivePack = selectedPacks.find(
            pack => isLockOutPack(pack) && !isSourceEnabledPack(pack)
        );

        if (exclusivePack) {
            return { type: 'lock-out-exclusive', packName: getPackName(exclusivePack) };
        }
        if (selectedPacks.some(isLockInPack)) return { type: 'lock-in' };
        if (selectedPacks.some(pack => isLockOutPack(pack) && isSourceEnabledPack(pack))) {
            return { type: 'lock-out-source' };
        }
        return null;
    };

    const isDisabledByLockRule = (pack) => {
        const packName = getPackName(pack);
        if (currentSelections.has(packName)) return false;

        const rule = getLockSelectionRule(currentSelections);
        if (!rule) return false;
        if (rule.type === 'lock-out-exclusive') return packName !== rule.packName;
        if (rule.type === 'lock-in') return !isLockInPack(pack);
        if (rule.type === 'lock-out-source') {
            return !(isLockOutPack(pack) && isSourceEnabledPack(pack));
        }
        return false;
    };

    const isDisabledByGlobalLockRule = (pack) => {
        if (!globalLockRule) return false;

        const packName = getPackName(pack);
        if (globalLockRule.type === 'lock-out-exclusive') {
            return !(
                globalLockRule.ownerIndex === index &&
                globalLockRule.packName === packName
            );
        }
        if (globalLockRule.type === 'lock-in') return !isLockInPack(pack);
        if (globalLockRule.type === 'lock-out-source') {
            return !(isLockOutPack(pack) && isSourceEnabledPack(pack));
        }
        return false;
    };

    const notifyGlobalLockRule = (selectionsByTile) => {
        const allSelections = new Set();
        Object.values(selectionsByTile).forEach(selections => {
            selections?.forEach(packName => allSelections.add(packName));
        });
        const rule = getLockSelectionRule(allSelections);
        onLockRuleChange?.(
            index,
            rule ? { ...rule, ownerIndex: index } : null
        );
    };

    useEffect(() => {
        if (currentSelections?.size > 0) {
            setShowTooltip(true);
            const timer = setTimeout(() => setShowTooltip(false), 5000);
            return () => clearTimeout(timer);
        } else {
            setShowTooltip(false);
        }
    }, [currentSelections.size, activeTile]);

    useEffect(() => {
        console.log('Packs state changed:', { packsCount: packs.length, currentPage, activeTile, firstPack: packs[0]?.packName });
    }, [packs, currentPage, activeTile]);

    const tileClick = async (tile, page = 1) => {
        const packType = tile?.packType;
        const cacheKey = `${packType}-${page}`;

        if (activeTile === packType && currentPage === page) {
            console.log('Same tile and page, skipping');
            return;
        }

        setSubFilters({});
        setActiveTile(packType);
        setCurrentPage(page);
        setRenderKey(prev => prev + 1);
        scrollRef.current?.scrollTo(0, 0);

        if (cachedData[cacheKey]) {
            console.log('Loading from cache:', cacheKey, cachedData[cacheKey]);
            const cached = cachedData[cacheKey];
            if (cached.mapping) {
                setMapping(cached.mapping);
            }
            if (cached.headingOrder) {
                setHeadingOrder(cached.headingOrder);
            }
            setTotalPages(cached.totalPages);
            setIsAddRequest(cached.isAddRequest);
            setIsDropRequest(cached.isDropRequest);
            setCurrentTitle(cached.name);
            const selections = cachedSelections[packType] || new Set();
            const sortedPacks = [...cached.packs].sort((a, b) => {
                const aName = a?.packName || a?.serviceType || `${a?.startDate}-${a?.mbr}`;
                const bName = b?.packName || b?.serviceType || `${b?.startDate}-${b?.mbr}`;
                const aSelected = selections.has(aName);
                const bSelected = selections.has(bName);
                if (aSelected && !bSelected) return -1;
                if (!aSelected && bSelected) return 1;
                return 0;
            });
            setPacks(sortedPacks);
            return;
        }

        try {
            setLoadingDetails(true);
            setPacks([]);
            const apiParams = { packType, sessionId };
            if (page > 1) apiParams.startFrom = page - 1;
            if(page === 0) apiParams.startFrom = - 1;
            console.log('Calling API with:', apiParams);
            const response = await apiService.getPackDetails(apiParams);
            console.log('API Response:', response);
            const packDetailsArray = response?.data?.packDetails || response?.packDetails;
            const packData = Array.isArray(packDetailsArray) ? packDetailsArray[0] : packDetailsArray;
            console.log('Pack Data:', packData);
            if (packData?.packs && Array.isArray(packData.packs)) {
                const apiMapping = packData?.mapping || {};
                const apiHeadingOrder = packData?.headingOrder || [];
                const hasNewFields = apiMapping.channelNumber && apiMapping.language && apiMapping.genre;
                let mergedMapping = hasNewFields ? apiMapping : { ...initialMapping, ...apiMapping };
                if (mergedMapping.services && mergedMapping.packName) {
                    delete mergedMapping.services;
                }
                setMapping(mergedMapping);
                if (apiHeadingOrder.length > 0) {
                    setHeadingOrder(apiHeadingOrder);
                }
                const cacheData = {
                    packs: packData.packs,
                    name: packData.header || tile.packTypeHeader || packType,
                    isAddRequest: !!packData.isAddRequest,
                    isDropRequest: !!packData.isDropRequest,
                    disabledCheckBox: !!packData.disabledCheckBox,
                    totalPages: packData.totalPages || 1,
                    mapping: mergedMapping,
                    headingOrder: apiHeadingOrder.length > 0 ? apiHeadingOrder : initialHeadingOrder
                };
                console.log('Setting packs:', cacheData);
                setCachedData(prev => ({ ...prev, [cacheKey]: cacheData }));
                setTotalPages(cacheData.totalPages);
                setIsAddRequest(cacheData.isAddRequest);
                setIsDropRequest(cacheData.isDropRequest);
                setCurrentTitle(cacheData.name);
                const selections = cachedSelections[packType] || new Set();
                const sortedPacks = [...packData.packs].sort((a, b) => {
                    const aName = a?.packName || a?.serviceType || `${a?.startDate}-${a?.mbr}`;
                    const bName = b?.packName || b?.serviceType || `${b?.startDate}-${b?.mbr}`;
                    const aSelected = selections.has(aName);
                    const bSelected = selections.has(bName);
                    if (aSelected && !bSelected) return -1;
                    if (!aSelected && bSelected) return 1;
                    return 0;
                });
                setPacks(sortedPacks);
                console.log('State updated with packs count:', packData.packs.length);
            } else {
                console.log('No packs in response or invalid format', { packData, response });
            }
        } catch (e) {
            console.error('Failed fetching pack details', e);
        } finally {
            setLoadingDetails(false);
        }
    };

    const toggleSelect = (pack, skipPopup = false) => {
        const packName = getPackName(pack);
        if (!packName) return;

        let popupType = null;
        if (pack?.raiseFdo) {
            popupType = 'raise-fdo';
        } else if (isLockInPack(pack)) {
            popupType = 'lock-in';
        } else if (isLockOutPack(pack) && !isSourceEnabledPack(pack)) {
            popupType = 'lock-out-exclusive';
        } else if (isLockOutPack(pack) && isSourceEnabledPack(pack)) {
            popupType = 'lock-out-source';
        }

        if (
            !skipPopup &&
            popupType &&
            pack?.isCheckBox &&
            !currentSelections.has(packName)
        ) {
            setFdoPopup({ pack, packName, type: popupType });
            return;
        }


        setCachedSelections((prev) => {
            const tileSelections = new Set(prev[activeTile] || []);
            if (tileSelections.has(packName)) {
                tileSelections.delete(packName);
                setFdoDisabledPacks(prevDisabled => {
                    const newDisabled = new Set(prevDisabled);
                    newDisabled.delete(packName);
                    if (newDisabled.size === 0) {
                        onFdoChange?.(false);
                    }
                    return newDisabled;
                });
            } else {
                if (isLockOutPack(pack) && !isSourceEnabledPack(pack)) {
                    tileSelections.clear();
                } else if (isLockInPack(pack)) {
                    Array.from(tileSelections).forEach(selectedName => {
                        if (!isLockInPack(findPackByName(selectedName))) {
                            tileSelections.delete(selectedName);
                        }
                    });
                } else if (isLockOutPack(pack) && isSourceEnabledPack(pack)) {
                    Array.from(tileSelections).forEach(selectedName => {
                        const selectedPack = findPackByName(selectedName);
                        if (!(isLockOutPack(selectedPack) && isSourceEnabledPack(selectedPack))) {
                            tileSelections.delete(selectedName);
                        }
                    });
                }
                tileSelections.add(packName);
            }

            const updated = { ...prev, [activeTile]: tileSelections };
            notifyGlobalLockRule(updated);

            const addPackList = [];
            const dropPackList = [];

            Object.entries(updated).forEach(([tile, selections]) => {
                const cacheKey = Object.keys(cachedData).find(k => k.startsWith(tile));
                const cached = cachedData[cacheKey];
                selections.forEach(name => {
                    if (cached?.isAddRequest || isAddRequest) addPackList.push(name);
                    if (cached?.isDropRequest || isDropRequest) dropPackList.push(name);
                });
            });

            setErrors((prevErrors) => {
                const updatedErrors = { ...prevErrors };
                if (addPackList.length === 0 && dropPackList.length === 0) {
                    delete updatedErrors[index];
                } else {
                    const displayParts = [];
                    if (addPackList.length > 0) displayParts.push(`Requested Add Packs: ${addPackList.join(', ')}`);
                    if (dropPackList.length > 0) displayParts.push(`Requested Drop Packs: ${dropPackList.join(', ')}`);

                    updatedErrors[index] = {
                        key1: { addPackList, dropPackList },
                        displayName: displayParts.join('\n'),
                        addPackList,
                        dropPackList,
                        enableRaiseFDO: addPackList.length > 0 && dropPackList.length > 0
                    };
                }
                return updatedErrors;
            });
            return updated;
        });
    };

    const setSubFilter = (tableKey, header, setVal) => {
        const key = `${tableKey}::${header}`;
        setSubFilters((prev) => ({ ...prev, [key]: setVal }));
    };

    const getSubFilter = (tableKey, header) => {
        const key = `${tableKey}::${header}`;
        return subFilters[key] ?? new Set();
    };

    const toggleCollapse = () => setCollapsed(!collapsed);

    const renderSubTable = (table, title, tableKey) => {
        if (!table) return null;

        const headerUniqueValues = {};
        table.headings.forEach((h, idx) => {
            const vals = idx === 0
                ? table.data.map((row) => row.optionName)
                : table.data.map((row) => {
                    const count = row.channelCount?.noOfChannels || row.channelCount?.total;
                    return typeof count === 'object' ? '' : count;
                }).filter(v => v !== '');
            headerUniqueValues[h] = Array.from(new Set(vals));
        });

        const filteredData = table.data.filter((row) => {
            const nameSet = getSubFilter(tableKey, table.headings[0]);
            const countSet = getSubFilter(tableKey, table.headings[1]);
            const nameMatch = !nameSet || nameSet.size === 0 || nameSet.has(row.optionName);
            const countVal = row.channelCount?.noOfChannels || row.channelCount?.total;
            const countMatch = !countSet || countSet.size === 0 || countSet.has(countVal);
            return nameMatch && countMatch;
        });

        return (
            <div style={{ height: !collapsed && "250px" }} className="sub-table table-scroll">
                <h4
                    onClick={toggleCollapse}
                    style={{
                        cursor: "pointer",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "space-between",
                        padding: !collapsed && "20px"
                    }}
                >
                    <span>{title}</span>
                    <span className="toggle">{collapsed ? <FaPlus /> : <FaMinus />}</span>
                </h4>

                {!collapsed && (
                    <table>
                        <thead>
                            <tr>
                                {table.headings.map((h, idx) => (
                                    <th key={idx}>
                                        <div className="th-inner A3f">
                                            <span>{h}</span>
                                            <FilterDropdown
                                                values={headerUniqueValues[h]}
                                                selected={getSubFilter(tableKey, h)}
                                                onChange={(setVal) => setSubFilter(tableKey, h, setVal)}
                                                placeholder={`Search ${h}`}
                                            />
                                        </div>
                                    </th>
                                ))}
                            </tr>
                        </thead>

                        <tbody>
                            {filteredData.map((row, idx) => {
                                const nameSet = getSubFilter(tableKey, table?.headings[0]);
                                const countSet = getSubFilter(tableKey, table?.headings[1]);

                                const nameHighlighted = nameSet?.size > 0 && nameSet.has(row?.optionName);
                                const countVal = row.channelCount?.noOfChannels || row.channelCount?.total;
                                const countHighlighted = countSet?.size > 0 && countSet.has(countVal);

                                return (
                                    <tr key={idx}>
                                        <td style={nameHighlighted ? { color: 'red', fontWeight: 'bold' } : {}}>
                                            {row.optionName}
                                        </td>

                                        <td className="channel-count" style={countHighlighted ? { color: 'red', fontWeight: 'bold' } : {}}>
                                            {row?.channelCount && (
                                                <div className="channel-count-wrapper-popup">
                                                    <span className="hannel-count-total">
                                                        {row?.channelCount?.noOfChannels || row?.channelCount?.total}
                                                    </span>

                                                    <div className="hannel-count-group">
                                                        <span className="hannel-count-box">
                                                            {row?.channelCount?.noOfHDChannels || row?.channelCount?.hd}
                                                        </span>
                                                        <span className="hannel-count-pill">HD</span>
                                                    </div>

                                                    <div className="hannel-count-group">
                                                        <span className="hannel-count-box">
                                                            {row?.channelCount?.noOfSDChannels || row?.channelCount?.sd}
                                                        </span>
                                                        <span className="hannel-count-pill">SD</span>
                                                    </div>
                                                </div>
                                            )}
                                        </td>
                                    </tr>
                                );
                            })}
                        </tbody>
                    </table>
                )}
            </div>
        );
    };

    const Modal = ({ children, onClose }) => {
        const ref = useRef();
        const handleClickInside = (e) => e.stopPropagation();

        return createPortal(
            <div className="filter-overlay" onClick={onClose}>
                <div className="filter-dropdown-modal" ref={ref} onClick={handleClickInside}>
                    {children}
                </div>
            </div>,
            document.body
        );
    };

    const renderChannelTable = (channelList) => {
        if (!channelList) return null;
        const headers = channelList.listOfHeadings || [];
        let allRows = [];
        if (Array.isArray(channelList?.channelData)) allRows = channelList.channelData.flatMap((block) => block.data || []);
        else if (channelList?.channelData?.data) allRows = channelList.channelData.data;

        const headerUniqueValues = {};
        headers.forEach((h, idx) => {
            headerUniqueValues[h] = Array.from(new Set(allRows.map((row) => {
                const val = row[idx];
                return typeof val === 'object' ? '' : val;
            }).filter(v => v !== '')));
        });

        const filteredRows = allRows.filter((row) => {
            return headers.every((h, idx) => {
                const set = getSubFilter('channelList', h);
                if (!set || set.size === 0) return true;
                const val = typeof row[idx] === 'object' ? '' : row[idx];
                return set.has(val);
            });
        });

        return (
            <div className="sub-table table-scroll">
                <h4>{channelList?.name}</h4>
                <table>
                    <thead>
                        <tr>
                            {headers.map((h, idx) => (
                                <th key={idx}>
                                    <div className="th-inner A3f">
                                        <span>{h}</span>
                                        <FilterDropdown
                                            values={headerUniqueValues[h]}
                                            selected={getSubFilter('channelList', h)}
                                            onChange={(setVal) => setSubFilter('channelList', h, setVal)}
                                            placeholder={`Search ${h}`}
                                        />
                                    </div>
                                </th>
                            ))}
                        </tr>
                    </thead>
                    <tbody>
                        {filteredRows.map((row, rIdx) => (
                            <tr key={rIdx}>
                                {row.map((col, cIdx) => (
                                    <td key={cIdx}>
                                        {typeof col === 'object' ? '' : col}
                                    </td>
                                ))}
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>
        );
    };

    if (!showMaximumChat && !showMaxScreen) {
        return (
            <div className="a3pack-widget">
                <button
                    className="fullscreen-btn"
                    onClick={() => setIsShowMaximumChat(true)}
                >
                    Click here to open full screen
                </button>
            </div>
        );
    }

    return (
        <div style={{ position: 'relative' }}>
            <div className="a3pack-container explore-packs-container">
            <div className="section">
                <div style={{ background: 'linear-gradient(135deg, #FFE5F4 0%, #FFF0F9 100%)', padding: '16px', borderRadius: '8px', marginBottom: exploreTiles.length > 0 ? '16px' : '0', border: '2px solid #FFB3D9', marginTop: '16px', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <h3 style={{ margin: 0, color: '#D02F92', fontWeight: 'bold', fontSize: '18px', cursor: 'pointer' }} onClick={() => setIsHeaderCollapsed(!isHeaderCollapsed)}>{currentTitle || header}</h3>
                    {packDetails?.isYearAndMonthEnabled && (
                        <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
                            <select value={selectedMonth} onChange={async (e) => {
                                const month = e.target.value;
                                setSelectedMonth(month);
                                setPacks([]);
                                setNoDataMessage('');
                                try {
                                    const response = await apiService.fetchPreviousHistory({ sessionId, month: String(month).padStart(2, '0'), year: selectedYear });
                                    const packData = Array.isArray(response?.packDetails) ? response?.packDetails[0] : response?.packDetails;
                                    if (packData?.packs.length === 0) {
                                        setNoDataMessage(response.transMessage);
                                    } else if (response?.packDetails) {
                                        setPacks(packData.packs);
                                    }
                                } catch (e) { console.error(e); }
                            }} style={{ padding: '6px 12px', borderRadius: '6px', border: '1px solid #D02F92' }}>
                                {Array.from({ length: 12 }, (_, i) => i + 1).map(m => <option key={m} value={m}>{String(m).padStart(2, '0')}</option>)}
                            </select>
                            <select value={selectedYear} onChange={async (e) => {
                                const year = e.target.value;
                                setSelectedYear(year);
                                setPacks([]);
                                setNoDataMessage('');
                                try {
                                    const response = await apiService.fetchPreviousHistory({ sessionId, month: String(selectedMonth).padStart(2, '0'), year });
                                    const packData = Array.isArray(response?.packDetails) ? response?.packDetails[0] : response?.packDetails;
                                    if (packData?.packs.length === 0) {
                                        setNoDataMessage(response.transMessage);
                                    } else if (response?.packDetails) {
                                        setPacks(packData.packs);
                                    }
                                } catch (e) { console.error(e); }
                            }} style={{ padding: '6px 12px', borderRadius: '6px', border: '1px solid #D02F92' }}>
                                {Array.from({ length: 10 }, (_, i) => new Date().getFullYear() - i).map(y => <option key={y} value={y}>{y}</option>)}
                            </select>
                        </div>
                    )}
                    <img src={isHeaderCollapsed ? iconPlus : iconMinus} alt="toggle" style={{ width: '24px', height: '24px', cursor: 'pointer' }} onClick={() => setIsHeaderCollapsed(!isHeaderCollapsed)} />
                </div>
                <div style={{ 
                    maxHeight: isHeaderCollapsed ? '0' : '2000px',
                    opacity: isHeaderCollapsed ? 0 : 1,
                    overflow: isHeaderCollapsed ? 'hidden' : 'visible',
                    transition: 'max-height 0.5s ease, opacity 0.4s ease',
                    marginBottom : '20px'
                }}>
                {exploreTiles.length > 0 && (
                    <div style={{ border: '2px solid #e0e0e0', borderRadius: '12px', padding: '16px', marginBottom: '20px', background: '#fafafa', position: 'relative', minHeight: '180px' }}>
                        <ScrollArrows containerRef={tilesScrollRef} />
                        <div ref={tilesScrollRef} className="explore-packs__tiles action-tab">
                            {exploreTiles.map((tile, idx) => {
                                const hasSelections = cachedSelections[tile.packType]?.size > 0;
                                return (
                                    <button
                                        key={`${tile.packType}-${idx}`}
                                        className={`explore-packs__tile ${activeTile === tile.packType ? 'active' : ''} ${hasSelections ? 'selected' : ''} action-tab` }
                                        onClick={() => tileClick(tile , 0)}
                                        disabled={loadingDetails || disabled}
                                        title={tile.packTypeDesc}
                                        style={{ opacity: disabled ? 0.5 : 1, cursor: disabled ? 'not-allowed' : 'pointer' }}
                                    >
                                        {tile.imageLink && <img src={tile.imageLink} alt={tile.packTypeHeader || tile.packType} />}
                                        <span className="explore-packs__tile-label">{tile.packTypeHeader || tile.packType}</span>
                                    </button>
                                );
                            })}
                        </div>
                    </div>
                )}

                <div style={{ border: '2px solid #e0e0e0', borderRadius: '12px', overflow: 'hidden', background: '#fff', position: 'relative' }}>
                    {noDataMessage || packDetails?.packs.length === 0  ? (
                        <div style={{ 
                            padding: '40px 20px', 
                            textAlign: 'center', 
                            color: '#666',
                            fontSize: '16px',
                            fontWeight: '500'
                        }}>
                            {noDataMessage || "No data Found"}
                        </div>
                    ) : (
                    <>
                    <ScrollArrows containerRef={scrollRef} />
                    <div
                        ref={scrollRef}
                        className="explore-table-scroll"
                        style={{ maxHeight: '400px', overflowY: 'auto' }}
                        onScroll={(event) => setIsTableHorizontallyScrolled(event.currentTarget.scrollLeft > 1)}
                    >
                        {loadingDetails ? (
                            <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '200px' }}>
                                <div style={{ fontSize: '18px', color: '#D02F92' }}>Loading...</div>
                            </div>
                        ) : (
                        <div
                            key={renderKey}
                            className={`main-table-div explore-packs-table ${isTableHorizontallyScrolled ? 'is-horizontally-scrolled' : ''}`}
                        >
                            <div className="table-row header-row" style={{ position: 'sticky', top: 0, background: '#f8f8f8', zIndex: 10 }}>
                                {hasAnyCheckbox && <div className="table-cell chk-col action-tab sticky-visible-column sticky-visible-column-1">
                                    {currentSelections.size > 0 && (
                                        <>
                                            <button
                                                onClick={() => {
                                                    setCachedSelections(prev => {
                                                        const updated = { ...prev, [activeTile]: new Set() };
                                                        notifyGlobalLockRule(updated);
                                                        setErrors(prevErrors => {
                                                            const updatedErrors = { ...prevErrors };
                                                            delete updatedErrors[index];
                                                            return updatedErrors;
                                                        });
                                                        return updated;
                                                    });
                                                    setFdoDisabledPacks(new Set());
                                                    onFdoChange?.(false);
                                                }}
                                                style={{ background: 'none', border: 'none', cursor: 'pointer', fontSize: '18px', color: '#D02F92', fontWeight: 'bold' }}
                                                title="Deselect All"
                                            >
                                                ✕
                                            </button>
                                            {showTooltip && (
                                                <div style={{ position: 'absolute', top: '100%', left: '50%', transform: 'translateX(-50%)', background: '#333', color: '#fff', padding: '4px 8px', borderRadius: '4px', fontSize: '11px', whiteSpace: 'nowrap', zIndex: 1000, marginTop: '4px' }}>
                                                    Deselect All
                                                </div>
                                            )}
                                        </>
                                    )}
                                </div>}
                                {Object.entries(orderedMapping).map(([key, headerText], idx) => {
                                    const headerUniqueValues = Array.from(new Set(packs.map(p => {
                                        const val = p[key];
                                        if (key === 'channelCount' && typeof val === 'object') return val?.total;
                                        if (typeof val === 'object') return '';
                                        return val;
                                    }).filter(v => v !== undefined && v !== null && v !== '')));
                                    const sortableHeaders = ['startDate', 'endDate', 'channelCount', 'mbr', 'channelNumber'];
                                    const isSortable = sortableHeaders.includes(key);
                                    const isActive = sortConfig.key === key;
                                    return (
                                        <div
                                            key={idx}
                                            className={`table-cell ${(idx + (hasAnyCheckbox ? 1 : 0)) < 3 ? `sticky-visible-column sticky-visible-column-${idx + (hasAnyCheckbox ? 2 : 1)}` : ''}`}
                                        >
                                            <div className="th-inner A3f">
                                                <span>{headerText}</span>
                                                {key !== 'channelList' && !popUpComponent.includes(key) && (
                                                    <div className="filter-sort-wrap">
                                                        <FilterDropdown
                                                            values={headerUniqueValues}
                                                            selected={getSubFilter('main', key)}
                                                            onChange={(setVal) => setSubFilter('main', key, setVal)}
                                                            placeholder={`Search ${headerText}`}
                                                        />
                                                        {isSortable && (
                                                            <button
                                                                type="button"
                                                                className="sort-arrow-btn"
                                                                onClick={() => {
                                                                    const newOrder = isActive && sortConfig.order === 'asc' ? 'desc' : 'asc';
                                                                    setSortConfig({ key, order: newOrder });
                                                                }}
                                                                title={`Sort ${isActive && sortConfig.order === 'asc' ? 'Descending' : 'Ascending'}`}
                                                            >
                                                                {isActive ? (sortConfig.order === 'asc' ? '↓' : '↑') : '↕'}
                                                            </button>
                                                        )}
                                                    </div>
                                                )}
                                            </div>
                                        </div>
                                    );
                                })}
                            </div>
                            {Array.isArray(packs) && packs.filter(pack => {
                                return Object.keys(orderedMapping).every(key => {
                                    const set = getSubFilter('main', key);
                                    if (!set || set.size === 0) return true;
                                    if (key === 'channelCount' && typeof pack[key] === 'object') {
                                        return set.has(pack[key]?.total);
                                    }
                                    return set.has(pack[key]);
                                });
                            }).sort((a, b) => {
                                if (!sortConfig.key) return 0;
                                const key = sortConfig.key;
                                let valA = a[key];
                                let valB = b[key];
                                if (key === 'channelCount') {
                                    valA = typeof a[key] === 'object' ? (a[key]?.total || 0) : 0;
                                    valB = typeof b[key] === 'object' ? (b[key]?.total || 0) : 0;
                                } else if (key === 'mbr') {
                                    const extractPrice = (val) => {
                                        if (!val) return 0;
                                        const match = String(val).match(/[\d,]+\.?\d*/);
                                        return match ? parseFloat(match[0].replace(/,/g, '')) : 0;
                                    };
                                    valA = extractPrice(valA);
                                    valB = extractPrice(valB);
                                } else if (key === 'channelNumber') {
                                    valA = parseInt(valA) || 0;
                                    valB = parseInt(valB) || 0;
                                    return sortConfig.order === 'asc' ? valA - valB : valB - valA;
                                } else if (key === 'startDate' || key === 'endDate') {
                                    const parseDate = (val) => {
                                        if (!val) return new Date(0);
                                        const parts = String(val).split('/');
                                        if (parts.length === 3) {
                                            const [day, month, year] = parts.map(Number);
                                            return new Date(year, month - 1, day);
                                        }
                                        return new Date(0);
                                    };
                                    valA = parseDate(valA);
                                    valB = parseDate(valB);
                                    return sortConfig.order === 'asc' ? valA - valB : valB - valA;
                                }
                                const isNumeric = typeof valA === 'number' && typeof valB === 'number';
                                if (isNumeric) return sortConfig.order === 'asc' ? valA - valB : valB - valA;
                                return sortConfig.order === 'asc' ? String(valA).localeCompare(String(valB)) : String(valB).localeCompare(String(valA));
                            }).map((pack) => {
                                const packName = pack.packName || pack.serviceType || `${pack.startDate}-${pack.mbr}`;
                                const checked = currentSelections.has(packName);
                                const cacheKey = `${activeTile}-${currentPage}`;
                                const currentDisabledCheckBox = cachedData[cacheKey]?.disabledCheckBox || false;
                                const lockRuleDisabled = isDisabledByLockRule(pack);
                                const globalLockRuleDisabled = isDisabledByGlobalLockRule(pack);
                                const checkboxDisabled =
                                    (disabled && !checked) ||
                                    (fdoDisabledPacks.size > 0 && !fdoDisabledPacks.has(packName)) ||
                                    lockRuleDisabled ||
                                    globalLockRuleDisabled;
                                const rowBackground = checked || (!pack.isCheckBox && !currentDisabledCheckBox) ? '#d4edda' : '#fff';
                                return (
                                    <div
                                        key={packName}
                                        className="table-row"
                                        style={{ backgroundColor: rowBackground, '--explore-row-background': rowBackground }}
                                    >
                                        {hasAnyCheckbox && (
                                            <div className="table-cell chk-col action-tab sticky-visible-column sticky-visible-column-1">
                                                {pack.isCheckBox && (
                                                    <input
                                                        type="checkbox"
                                                        checked={checked}
                                                        disabled={checkboxDisabled}
                                                        onChange={(e) => {
                                                            e.stopPropagation();
                                                            toggleSelect(pack);
                                                        }}
                                                        style={{
                                                            opacity: checkboxDisabled ? 0.5 : 1,
                                                            cursor: checkboxDisabled ? 'not-allowed' : 'pointer'
                                                        }}
                                                    />
                                                )}
                                            </div>
                                        )}
                                        {Object.keys(orderedMapping).map((key, cIdx) => {
                                            if (key === 'channelCount') {
                                                return (
                                                    <div key={cIdx} className={`table-cell channel-count ${(cIdx + (hasAnyCheckbox ? 1 : 0)) < 3 ? `sticky-visible-column sticky-visible-column-${cIdx + (hasAnyCheckbox ? 2 : 1)}` : ''}`}>
                                                        {pack.channelCount && typeof pack.channelCount === 'object' && (
                                                            <div className="channel-count-wrapper">
                                                                <span className="hannel-count-total">{pack.channelCount.total || ''}</span>
                                                                {pack.channelCount.hd && (
                                                                    <div className="hannel-count-group">
                                                                        <span className="hannel-count-box">{pack.channelCount.hd}</span>
                                                                        <span className="hannel-count-pill">HD</span>
                                                                    </div>
                                                                )}
                                                                {pack.channelCount.sd && (
                                                                    <div className="hannel-count-group">
                                                                        <span className="hannel-count-box">{pack.channelCount.sd}</span>
                                                                        <span className="hannel-count-pill">SD</span>
                                                                    </div>
                                                                )}
                                                            </div>
                                                        )}
                                                    </div>
                                                );
                                            }
                                            if (key === 'channelList' && pack.channelList) {
                                                return (
                                                    <div key={cIdx} className={`table-cell ${(cIdx + (hasAnyCheckbox ? 1 : 0)) < 3 ? `sticky-visible-column sticky-visible-column-${cIdx + (hasAnyCheckbox ? 2 : 1)}` : ''}`}>
                                                        <button
                                                            type="button"
                                                            className="link-btn"
                                                            onClick={() => setModalData(pack.channelList)}
                                                        >
                                                            Click Here
                                                        </button>
                                                    </div>
                                                );
                                            }
                                            if (popUpComponent.includes(key)) {
                                                const raw = pack[key];
                                                return (
                                                    <div key={cIdx} className={`table-cell ${(cIdx + (hasAnyCheckbox ? 1 : 0)) < 3 ? `sticky-visible-column sticky-visible-column-${cIdx + (hasAnyCheckbox ? 2 : 1)}` : ''}`}>
                                                        <button
                                                            type="button"
                                                            className="link-btn"
                                                            onClick={() => {
                                                                try {
                                                                    const parsed = typeof raw === 'string' ? JSON.parse(raw) : raw;
                                                                    if (parsed?.isHTMLContent) setUspPopup(parsed);
                                                                } catch {}
                                                            }}
                                                        >
                                                            View
                                                        </button>
                                                    </div>
                                                );
                                            }
                                            return (
                                                <div
                                                    key={cIdx}
                                                    className={`table-cell ${(cIdx + (hasAnyCheckbox ? 1 : 0)) < 3 ? `sticky-visible-column sticky-visible-column-${cIdx + (hasAnyCheckbox ? 2 : 1)}` : ''}`}
                                                    dangerouslySetInnerHTML={{ __html: typeof pack[key] === 'object' ? JSON.stringify(pack[key]) : (pack[key] || '') }}
                                                />
                                            );
                                        })}
                                    </div>
                                );
                            })}
                        </div>
                         )}
                    </div>
                    </>
                    )}
                </div>
                </div>
                {totalPages > 1 && (
                    <div style={{ display: 'flex', gap: '8px', justifyContent: 'center', padding: '16px', alignItems: 'center' }}>
                        <button
                            onClick={() => {
                                console.log('Previous clicked', { activeTile, currentPage });
                                if (activeTile && currentPage > 1) {
                                    const tile = exploreTiles.find(t => t.packType === activeTile);
                                    console.log('Found tile:', tile);
                                    if (tile) tileClick(tile, currentPage - 1);
                                }
                            }}
                            disabled={currentPage === 1 || loadingDetails}
                            style={{ padding: '8px 16px', borderRadius: '6px', border: '1px solid #D02F92', background: currentPage === 1 ? '#f0f0f0' : '#fff', cursor: currentPage === 1 ? 'not-allowed' : 'pointer' }}
                        >
                            Previous
                        </button>
                        {Array.from({ length: totalPages }, (_, i) => i + 1).map(page => {
                            const cacheKey = `${activeTile}-${page}`;
                            const hasSelections = cachedData[cacheKey] && cachedSelections[activeTile]?.size > 0 &&
                                Array.from(cachedSelections[activeTile]).some(packName =>
                                    cachedData[cacheKey]?.packs?.some(p => p.packName === packName)
                                );
                            return (
                                <button
                                    key={page}
                                    onClick={() => {
                                        console.log('Page clicked', { page, activeTile });
                                        if (activeTile) {
                                            const tile = exploreTiles.find(t => t.packType === activeTile);
                                            console.log('Found tile:', tile);
                                            if (tile) tileClick(tile, page);
                                        }
                                    }}
                                    disabled={loadingDetails}
                                    style={{
                                        padding: '8px 12px',
                                        borderRadius: '6px',
                                        border: '1px solid #D02F92',
                                        background: currentPage === page ? '#D02F92' : (hasSelections ? '#d4edda' : '#fff'),
                                        color: currentPage === page ? '#fff' : '#D02F92',
                                        fontWeight: currentPage === page ? 'bold' : 'normal',
                                        cursor: 'pointer'
                                    }}
                                >
                                    {page}
                                </button>
                            );
                        })}
                        <button
                            onClick={() => {
                                console.log('Next clicked', { activeTile, currentPage, totalPages });
                                if (activeTile && currentPage < totalPages) {
                                    const tile = exploreTiles.find(t => t.packType === activeTile);
                                    console.log('Found tile:', tile);
                                    if (tile) tileClick(tile, currentPage + 1);
                                }
                            }}
                            disabled={currentPage === totalPages || loadingDetails}
                            style={{ padding: '8px 16px', borderRadius: '6px', border: '1px solid #D02F92', background: currentPage === totalPages ? '#f0f0f0' : '#fff', cursor: currentPage === totalPages ? 'not-allowed' : 'pointer' }}
                        >
                            Next
                        </button>
                    </div>
                )}
            </div>

            {uspPopup && (
                <Modal onClose={() => setUspPopup(null)}>
                    <div className="modal-header">
                        <button className="close-modal-btn" onClick={() => setUspPopup(null)}>✕</button>
                    </div>
                    <div className="expanded-wrap">
                        <div dangerouslySetInnerHTML={{ __html: uspPopup.htmlScript }} />
                    </div>
                </Modal>
            )}

            {modalData && (
                <Modal onClose={() => setModalData(null)}>
                    <div className="modal-header">
                        <button className="clear-filters" onClick={() => {
                            const headers = modalData.listOfHeadings || [];
                            headers.forEach(h => setSubFilter('channelList', h, new Set()));
                        }}>
                            Clear All Filters
                        </button>
                        <button className="close-modal-btn" onClick={() => {
                            const headers = modalData.listOfHeadings || [];
                            headers.forEach(h => setSubFilter('channelList', h, new Set()));
                            setModalData(null);
                        }}>✕</button>
                    </div>
                    <div className="expanded-wrap">
                        <div className="disclaimer-container">
                            <p>
                                <span className="highlight-red">Selected filters</span> are marked with
                                <span className="highlight-bg"> Red Font and Green Tick ( ✅ )</span>. To clear filters,
                                click <span className="highlight-blue">'Clear All Filters'</span> or close the window.
                            </p>
                        </div>
                        {(modalData?.languageTable || modalData?.genreTable) && (
                            <div className="sub-tables-row">
                                {renderSubTable(modalData?.languageTable, "Languages", "lang")}
                                {renderSubTable(modalData?.genreTable, "Genres", "genre")}
                            </div>
                        )}
                        {renderChannelTable(modalData)}
                    </div>
                </Modal>
            )}

            {fdoPopup && (
                <FdoPopup
                    fdoPopup={fdoPopup}
                    onClose={() => {
                        setFdoPopup(null);
                        onFdoChange?.(false);
                    }}
                    onConfirm={() => {
                        const usesGlobalExclusiveDisable =
                            fdoPopup?.type === 'raise-fdo' ||
                            fdoPopup?.type === 'lock-out-exclusive';

                        if (!usesGlobalExclusiveDisable) {
                            toggleSelect(fdoPopup?.pack, true);
                            setFdoPopup(null);
                            return;
                        }

                        setFdoDisabledPacks(new Set([fdoPopup?.packName]));
                        onFdoChange?.(true);
                        setCachedSelections((prev) => {
                            const updated = { ...prev, [activeTile]: new Set([fdoPopup?.packName]) };
                            if (fdoPopup?.type === 'lock-out-exclusive') {
                                onLockRuleChange?.(index, {
                                    type: 'lock-out-exclusive',
                                    packName: fdoPopup?.packName,
                                    ownerIndex: index
                                });
                            }
                            setErrors((prevErrors) => {
                                const updatedErrors = { ...prevErrors };
                                updatedErrors[index] = {
                                    key1: { addPackList: [], dropPackList: [fdoPopup?.packName] },
                                    displayName: `Requested Drop Packs: ${fdoPopup?.packName}`,
                                    addPackList: [],
                                    dropPackList: [fdoPopup?.packName],
                                    enableRaiseFDO: false
                                };
                                return updatedErrors;
                            });
                            return updated;
                        });
                        setFdoPopup(null);
                    }}
                />
            )}
            </div>
        </div>
    );
};

export default ExplorePacks;
